import { type DragEvent, useId, useState } from 'react'
import { GuestApiError, requestGuestUploadUrl } from '../lib/share-links-api.js'

type Props = {
  token: string
  guestToken: string
  onUploaded?: () => void
}

type UploadingItem = {
  id: string
  file: File
  loadedBytes: number
  totalBytes: number
  status: 'uploading' | 'done' | 'error'
  error?: string
}

const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

/**
 * ゲスト用アップロード（UPLOAD権限のみ）。
 *
 * - DnD + ファイル選択
 * - シンプルな単発UL（マルチパートはオーナー側 upload-client.ts に対し別パスのため、
 *   M5 では単発のみ。マルチパート対応は M6 以降で統合する）
 *   - ただしAPI 側は kind=multipart も返すので、必要に応じて切替する
 */
export function GuestUpload({ token, guestToken, onUploaded }: Props) {
  const inputId = useId()
  const [items, setItems] = useState<UploadingItem[]>([])
  const [isDragging, setIsDragging] = useState(false)

  const updateItem = (id: string, patch: Partial<UploadingItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)))
  }

  const enqueue = async (files: FileList | File[]) => {
    const list = Array.from(files)
    const toEnqueue: UploadingItem[] = list.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      loadedBytes: 0,
      totalBytes: file.size,
      status: 'uploading',
    }))
    setItems((prev) => [...prev, ...toEnqueue])

    for (const it of toEnqueue) {
      try {
        const issued = await requestGuestUploadUrl(token, guestToken, {
          originalName: it.file.name,
          sizeBytes: it.file.size,
          mimeType: it.file.type || 'application/octet-stream',
        })
        if (issued.upload.kind !== 'single') {
          // M5 ではマルチパート未対応。50MB を超える UL は SP 側で警告
          throw new Error('現在 50MB を超えるファイルは未対応です')
        }
        await putXhr(it.file, issued.upload.uploadUrl, (loaded, total) => {
          updateItem(it.id, { loadedBytes: loaded, totalBytes: total })
        })
        // R2 PUT 完了後、ゲストトークンで完了通知（uploadedAt 立て + オーナー通知メール送信）
        await fetch(`${API_BASE}/share/${token}/files/${issued.fileId}/complete`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${guestToken}` },
        }).catch(() => undefined) // 通知失敗はクリティカルではないので無視
        updateItem(it.id, { status: 'done' })
      } catch (err) {
        const msg = err instanceof GuestApiError ? err.message : (err as Error).message
        updateItem(it.id, { status: 'error', error: msg })
      }
    }
    onUploaded?.()
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files.length > 0) void enqueue(e.dataTransfer.files)
  }

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging ? 'border-primary bg-primary/5' : 'border-border'
        }`}
      >
        <p className="text-sm text-muted-foreground">ファイルをドラッグ&ドロップ または</p>
        <label
          htmlFor={inputId}
          className="inline-flex mt-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium cursor-pointer"
        >
          ファイルを選択
        </label>
        <input
          id={inputId}
          type="file"
          multiple
          className="sr-only"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) void enqueue(e.target.files)
            e.target.value = ''
          }}
        />
      </div>

      {items.length > 0 && (
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it.id} className="border border-border rounded-md p-3 bg-background space-y-1">
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium truncate">{it.file.name}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatBytes(it.loadedBytes)} / {formatBytes(it.totalBytes)}
                </span>
              </div>
              <div className="h-2 bg-muted rounded overflow-hidden">
                <div
                  className={`h-full ${
                    it.status === 'error'
                      ? 'bg-red-500'
                      : it.status === 'done'
                        ? 'bg-green-500'
                        : 'bg-primary'
                  }`}
                  style={{
                    width: `${
                      it.totalBytes === 0 ? 0 : Math.round((it.loadedBytes / it.totalBytes) * 100)
                    }%`,
                  }}
                />
              </div>
              {it.status === 'error' && it.error && (
                <p className="text-xs text-red-600">{it.error}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function putXhr(
  file: File,
  url: string,
  onProgress: (loaded: number, total: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total)
    })
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`R2 PUT failed: ${xhr.status}`))
    })
    xhr.addEventListener('error', () => reject(new Error('R2 PUT network error')))
    xhr.send(file)
  })
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
